import { useRef, useState, type CSSProperties } from 'react'
import type { DealsSearchFilterOption } from '@shopista/shared/types'
import { Checkbox, CycleCheckbox } from '../../components/Checkbox'
import { Modal } from '../../components/Modal'

// T55.17 — Categories cycle off → include → exclude, exactly like Attributes'
// off → require → exclude (same box anatomy, `CycleCheckbox`; different
// vocabulary word for the "positive" state, since a checked category is an
// alternative — OR within the section — not a constraint).
export type CategoryFilterIntent = 'include' | 'exclude'
export type AttributeFilterIntent = 'require' | 'exclude'
export interface StoreFilterOption { id: string; name: string; count: number }

export interface DealsFiltersDrawerProps {
  open: boolean
  resultCount: number
  optionSetCount: number
  categories: DealsSearchFilterOption[]
  attributes: DealsSearchFilterOption[]
  stores: StoreFilterOption[]
  selectedCategories: Map<string, CategoryFilterIntent>
  selectedAttributes: Map<string, AttributeFilterIntent>
  selectedStores: Set<string>
  onCategoriesChange: (next: Map<string, CategoryFilterIntent>) => void
  onAttributesChange: (next: Map<string, AttributeFilterIntent>) => void
  onStoresChange: (next: Set<string>) => void
  onClose: () => void
}

type Tab = 'category' | 'attribute' | 'store'
const HAIRLINE = 'var(--border-width-hairline)'
const TOUCH = '2.75rem'
const bodyStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minHeight: '24rem' }
const headerStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', paddingBottom: 'var(--space-2)', borderBottom: `${HAIRLINE} solid var(--color-border-subtle)` }
const titleStyle: CSSProperties = { flex: 1, margin: 0, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }
const headerCloseStyle: CSSProperties = { display: 'inline-grid', placeItems: 'center', width: TOUCH, height: TOUCH, padding: 0, border: 'none', borderRadius: 'var(--radius-pill)', background: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-lg)', cursor: 'pointer' }
const tabsStyle: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-1)', borderBottom: `${HAIRLINE} solid var(--color-border-subtle)` }
const tabStyle: CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-1)', minHeight: TOUCH, padding: 'var(--space-1)', border: 'none', borderRadius: 'var(--radius-md)', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-2sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' }
const activeTabStyle: CSSProperties = { ...tabStyle, color: 'var(--color-accent-primary)', background: 'var(--color-accent-subtle)' }
const tabBadgeStyle: CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '1.125rem', height: '1.125rem', padding: '0 var(--space-1)', borderRadius: 'var(--radius-pill)', background: 'var(--color-bg-muted)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-3xs)' }
const activeTabBadgeStyle: CSSProperties = { ...tabBadgeStyle, background: 'var(--color-accent-primary)', color: 'var(--color-text-inverse)' }
// T55.15 — the drawer's own list is NO LONGER a scroll container (no
// `overflowY`/`flex`): Modal's shared sheet body (`Modal.tsx` `bodyByVariant.sheet`,
// unchanged) is the drawer's ONE vertical scroll owner (T55.11 root cause 9 had
// TWO: this list's own `overflowY:'auto'` was inert (its parent `bodyStyle` has no
// bounded height, so this container never actually needed to scroll internally) but
// STILL counted as "a scroll container" for `position: sticky` resolution purposes —
// so the search-box tools below stuck to THIS (motionless) box instead of Modal's
// real, scrolling body, and visibly rode away with the rest of the content instead of
// staying pinned. Removing this container's own overflow lets `position: sticky`
// resolve past it to the real scrolling ancestor. `overflow:'hidden'` (both axes, not
// just `overflowX`) is the horizontal-overflow backstop for this boundary — setting
// only `overflowX` here would make the browser force `overflowY` to compute as `auto`
// (the CSS "mismatched overflow pair" rule), silently reintroducing a second
// (inert-but-sticky-breaking) scroll container. `overflowY:'hidden'` is a no-op for
// clipping (this box's height is always exactly its content's height), so it's safe.
const listStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0, overflow: 'hidden' }
// T55.15 — combines the header, the tab strip, and the active tab's tools (label +
// caption + search box) into ONE sticky block so they read as a single pinned unit
// (no per-piece `top` offset math needed) while `listStyle`'s rows scroll underneath,
// via Modal's shared sheet body scroll. `overflow:'hidden'` here is a safe horizontal
// backstop — unlike `bodyStyle` (see below), nothing inside this wrapper needs to
// resolve `position: sticky` against an ancestor ABOVE it, so marking this element a
// scroll container has no side effect on stickiness.
const toolsClusterStyle: CSSProperties = { position: 'sticky', top: 0, zIndex: 'var(--z-raised)' as unknown as number, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minWidth: 0, overflow: 'hidden', background: 'var(--color-bg-raised)' }
// The per-tab tools block (label/caption + search box) rendered INSIDE
// `toolsClusterStyle` — no longer sticky/positioned itself (the wrapper handles that).
const tabToolsStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }
const sectionLabelStyle: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }
const sectionTitleStyle: CSSProperties = { color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }
const sectionCaptionStyle: CSSProperties = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-3xs)' }
const searchStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minHeight: TOUCH, padding: '0 var(--space-2) 0 var(--space-3)', border: `${HAIRLINE} solid var(--color-border-strong)`, borderRadius: 'var(--radius-pill)', background: 'var(--color-bg-raised)' }
const searchInputStyle: CSSProperties = { flex: 1, minWidth: 0, border: 'none', background: 'transparent', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-sans)', fontSize: 'var(--font-size-sm)' }
const searchClearStyle: CSSProperties = { flex: '0 0 auto', display: 'inline-grid', placeItems: 'center', minWidth: TOUCH, minHeight: TOUCH, padding: 0, border: 'none', borderRadius: 'var(--radius-pill)', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }
// T55.15 — `boxSizing:'border-box'` closes the classic `width:'100%'` + horizontal
// padding overflow (content-box default renders 2×padding wider than the container);
// `minWidth:0` removes this row's own flex-item auto min-content floor (it's a flex
// item of `listStyle`'s column layout) so long option copy can shrink/wrap instead of
// forcing the row — and everything below it — wider than the sheet.
// T55.17 — the interactive require/exclude/include row styles that used to
// live here (`activeRowStyle`/`excludeRowStyle`) moved into the shared
// `CycleCheckbox` component (`components/Checkbox.tsx`); `rowStyle` survives
// only as the base the non-interactive homogeneous info row below extends.
const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', minHeight: TOUCH, width: '100%', boxSizing: 'border-box', minWidth: 0, padding: '0 var(--space-3)', border: 'none', borderRadius: 'var(--radius-md)', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer', textAlign: 'left' }
const infoStyle: CSSProperties = { ...rowStyle, cursor: 'default', color: 'var(--color-text-muted)', background: 'var(--color-bg-secondary)' }
// The row's own term/count text are flex items of `rowStyle`'s row layout (a bare
// `<span>` defaults to `min-width:auto`, i.e. a floor at its own min-content size) —
// `minWidth:0` + `overflowWrap:'anywhere'` let long option copy wrap (or, for a
// single unbroken long token, break mid-word) instead of pushing the row wider.
const rowTermStyle: CSSProperties = { flex: 1, minWidth: 0, overflowWrap: 'anywhere' }
const checkboxRowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minHeight: TOUCH, boxSizing: 'border-box', minWidth: 0, padding: '0 var(--space-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-secondary)' }
const checkboxControlStyle: CSSProperties = { flex: 1, minWidth: 0 }
const countStyle: CSSProperties = { flexShrink: 1, minWidth: 0, overflowWrap: 'anywhere', fontSize: 'var(--font-size-3xs)', color: 'var(--color-text-muted)' }
const footerStyle: CSSProperties = { position: 'sticky', bottom: 0, display: 'flex', flexDirection: 'column', background: 'var(--color-bg-raised)' }
const closeStyle: CSSProperties = { width: '100%', minHeight: TOUCH, border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-primary)', color: 'var(--color-text-inverse)', fontWeight: 'var(--font-weight-bold)', cursor: 'pointer' }
const emptyStyle: CSSProperties = { padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-muted)' }

interface FilterSearchBoxProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  ariaLabel: string
  clearAriaLabel: string
  testIdPrefix: string
}

// T55.14 — the ONE search-box control shared by the Categories (the shipped
// T55.12 reference), Attributes, and Stores tabs: case-insensitive substring
// match over the shown option terms, instant (no debounce, no request), a
// clear button that restores the full list and refocuses the input. Purely
// presentational — the narrowed `value` lives in the caller's per-tab state
// (see `categorySearch`/`attributeSearch`/`storeSearch` below), so switching
// tabs never resets another tab's search text.
function FilterSearchBox(props: FilterSearchBoxProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div style={searchStyle}>
      <span aria-hidden="true">🔍</span>
      <input ref={inputRef} value={props.value} onChange={(event) => props.onChange(event.target.value)} placeholder={props.placeholder} aria-label={props.ariaLabel} style={searchInputStyle} data-testid={`${props.testIdPrefix}-search`} />
      {props.value.length > 0 && <button type="button" style={searchClearStyle} aria-label={props.clearAriaLabel} onClick={() => { props.onChange(''); inputRef.current?.focus() }} data-testid={`${props.testIdPrefix}-search-clear`}>✕</button>}
    </div>
  )
}

export function DealsFiltersDrawer(props: DealsFiltersDrawerProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('category')
  const [categorySearch, setCategorySearch] = useState('')
  const [attributeSearch, setAttributeSearch] = useState('')
  const [storeSearch, setStoreSearch] = useState('')

  // T55.12 — tab badges show the AVAILABLE selectable-option count (never
  // the active-selection count, which duplicates the outer `Filters (N)`
  // count + the active chips row and reads as "0 options" before anything
  // is picked). The badge's highlighted/default STYLE still tracks whether
  // this tab has an active selection — that visual cue is unrelated to what
  // number is printed inside it.
  const categoryCount = props.categories.length
  const attributeCount = props.attributes.length
  const storeCount = props.stores.length
  const categoryActive = props.selectedCategories.size > 0
  const attributeActive = props.selectedAttributes.size > 0
  const storeActive = props.selectedStores.size > 0
  const homogeneous = (count: number) => props.optionSetCount > 0 && count === props.optionSetCount
  // T55.14 — search narrows the RENDERED rows only; tab badges above stay on
  // the full `props.*` counts (unaffected by any of these three needles).
  const categoryNeedle = categorySearch.trim().toLowerCase()
  const shownCategories = categoryNeedle.length === 0
    ? props.categories
    : props.categories.filter((option) => option.term.toLowerCase().includes(categoryNeedle))
  const attributeNeedle = attributeSearch.trim().toLowerCase()
  const shownAttributes = attributeNeedle.length === 0
    ? props.attributes
    : props.attributes.filter((option) => option.term.toLowerCase().includes(attributeNeedle))
  const storeNeedle = storeSearch.trim().toLowerCase()
  const shownStores = storeNeedle.length === 0
    ? props.stores
    : props.stores.filter((store) => store.name.toLowerCase().includes(storeNeedle))

  // T55.17 — cycles off → include → exclude, mirroring `cycleAttribute`
  // below except for the vocabulary word ('include' vs 'require') for the
  // "positive" state.
  const cycleCategory = (term: string) => {
    const next = new Map(props.selectedCategories)
    const current = next.get(term)
    if (!current) next.set(term, 'include')
    else if (current === 'include') next.set(term, 'exclude')
    else next.delete(term)
    props.onCategoriesChange(next)
  }
  const cycleAttribute = (term: string) => {
    const next = new Map(props.selectedAttributes)
    const current = next.get(term)
    if (!current) next.set(term, 'require')
    else if (current === 'require') next.set(term, 'exclude')
    else next.delete(term)
    props.onAttributesChange(next)
  }
  const toggleStore = (id: string) => {
    const next = new Set(props.selectedStores)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    props.onStoresChange(next)
  }

  return (
    <Modal open={props.open} onClose={props.onClose} variant="sheet" ariaLabel="Filters" hideCloseButton keyboardAware>
      {/* T55.15 — `bodyStyle` deliberately carries NO `overflow`/`overflowX`/
          `overflowY` of its own. It's the real ancestor `position: sticky` on
          `toolsClusterStyle` resolves against (skipping over it) to reach Modal's
          shared, genuinely-bounded sheet body (`Modal.tsx` `bodyByVariant.sheet`,
          unchanged). Marking `bodyStyle` a scroll container here (even `overflow:
          'hidden'`) would make IT the sticky reference instead — and since this
          box has no bounded height of its own (same "inert scroller" trap this task
          fixes at the list level), that would silently reintroduce the exact bug
          one level up. */}
      <div style={bodyStyle} data-testid="deals-filters-drawer">
        <div style={toolsClusterStyle} data-testid="deals-filters-tools">
          <div style={headerStyle}>
            <h2 style={titleStyle}>Filters</h2>
            <button type="button" style={headerCloseStyle} aria-label="Close filters" onClick={props.onClose} data-testid="deals-filters-close">✕</button>
          </div>
          <div style={tabsStyle} role="tablist" aria-label="Filter sections">
            {([
              ['category', 'Categories', categoryCount, categoryActive],
              ['attribute', 'Attributes', attributeCount, attributeActive],
              ['store', 'Stores', storeCount, storeActive],
            ] as const).map(([id, label, count, active]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} style={tab === id ? activeTabStyle : tabStyle} onClick={() => setTab(id)} data-testid={`deals-filters-tab-${id}`}>
                <span>{label}</span><span style={active ? activeTabBadgeStyle : tabBadgeStyle} data-testid={`deals-filters-count-${id}`}>{count}</span>
              </button>
            ))}
          </div>
          {tab === 'category' && (
            <div style={tabToolsStyle}>
              {/* T55.17 — the caption now names the include/exclude cycle
                  (was "Choose any", written when Categories were a plain
                  multi-select). */}
              <div style={sectionLabelStyle}><b style={sectionTitleStyle}>Categories</b><span style={sectionCaptionStyle}>Tap to include or exclude · includes subcategories</span></div>
              <FilterSearchBox value={categorySearch} onChange={setCategorySearch} placeholder="Search categories…" ariaLabel="Search categories" clearAriaLabel="Clear category search" testIdPrefix="deals-filter-category" />
            </div>
          )}
          {/* T55.14 — Attributes are now exhaustive too (the counting module's
              `attributeOptions`, no server PER_SECTION_CAP=4); the search box
              narrows the rendered rows only. Tri-state cycling, the
              homogeneous info row, and styling are all unchanged. No search box
              at all when there are zero attribute options — matches the empty
              state below (nothing to search). */}
          {tab === 'attribute' && props.attributes.length > 0 && (
            <div style={tabToolsStyle}>
              <FilterSearchBox value={attributeSearch} onChange={setAttributeSearch} placeholder="Search attributes…" ariaLabel="Search attributes" clearAriaLabel="Clear attribute search" testIdPrefix="deals-filter-attribute" />
            </div>
          )}
          {/* T55.14 — Store rows are unchanged apart from the search box
              above them: same fixed selected-store list, same checkbox row,
              search narrows what's SHOWN only. */}
          {tab === 'store' && (
            <div style={tabToolsStyle}>
              <FilterSearchBox value={storeSearch} onChange={setStoreSearch} placeholder="Search stores…" ariaLabel="Search stores" clearAriaLabel="Clear store search" testIdPrefix="deals-filter-store" />
            </div>
          )}
        </div>
        <div style={listStyle} role="tabpanel" data-testid="deals-filters-list">
          {tab === 'category' && (
            /* T55.12/T55.17 — Categories are exhaustive and always
               selectable, no already-in-bar exclusion, no homogeneous info
               row (a Category every result carries is still a normal row —
               Attributes keep the info row below, unchanged). Rows now cycle
               off → include → exclude through the SAME shared `CycleCheckbox`
               control Attributes use (T55.17) — the box glyph anatomy that
               was missing here entirely. */
            props.categories.length === 0 ? <div style={emptyStyle}>No category options in these deals.</div> : shownCategories.length === 0 ? <div style={emptyStyle}>No categories match this search.</div> : shownCategories.map((option) => {
              const intent = props.selectedCategories.get(option.term)
              return <CycleCheckbox
                key={option.term}
                label={option.term}
                intent={intent === 'exclude' ? 'negative' : intent === 'include' ? 'positive' : 'off'}
                stateLabel={intent === 'exclude' ? 'Excluded' : intent === 'include' ? 'Included' : undefined}
                count={option.count}
                onClick={() => cycleCategory(option.term)}
                aria-label={`${option.term}: ${intent ?? 'off'}`}
                data-testid={`deals-filter-category-${option.term}`}
              />
            })
          )}
          {tab === 'attribute' && (
            props.attributes.length === 0 ? <div style={emptyStyle}>No attribute options in these deals.</div> : shownAttributes.length === 0 ? <div style={emptyStyle}>No attributes match this search.</div> : shownAttributes.map((option) => {
              const intent = props.selectedAttributes.get(option.term)
              if (homogeneous(option.count)) return <div key={option.term} style={infoStyle} data-testid={`deals-filter-attribute-info-${option.term}`}><span style={rowTermStyle}>{option.term}</span><span style={countStyle}>{option.count} · all results match these</span></div>
              // T55.17 — the shared `CycleCheckbox` control (was a bare
              // button with the glyph inline in the term text, no box, no
              // state label — the drift this task repairs).
              return <CycleCheckbox
                key={option.term}
                label={option.term}
                intent={intent === 'exclude' ? 'negative' : intent === 'require' ? 'positive' : 'off'}
                stateLabel={intent === 'exclude' ? 'Excluded' : intent === 'require' ? 'Required' : undefined}
                count={option.count}
                onClick={() => cycleAttribute(option.term)}
                aria-label={`${option.term}: ${intent ?? 'off'}`}
                data-testid={`deals-filter-attribute-${option.term}`}
              />
            })
          )}
          {tab === 'store' && shownStores.map((store) => (
            <div key={store.id} style={checkboxRowStyle}>
              <div style={checkboxControlStyle}><Checkbox checked={props.selectedStores.has(store.id)} onChange={() => toggleStore(store.id)} label={store.name} data-testid={`deals-filter-store-${store.id}`} /></div>
              <span style={countStyle}>{store.count}</span>
            </div>
          ))}
        </div>
        <div style={footerStyle}>
          <button type="button" style={closeStyle} onClick={props.onClose} data-testid="deals-filters-show">Show {props.resultCount} deals</button>
        </div>
      </div>
    </Modal>
  )
}

export interface ActiveFilterChipsProps {
  categories: Map<string, CategoryFilterIntent>
  attributes: Map<string, AttributeFilterIntent>
  stores: Array<{ id: string; name: string }>
  selectedStores: Set<string>
  onRemoveCategory: (term: string) => void
  onRemoveAttribute: (term: string) => void
  onRemoveStore: (id: string) => void
  onClear: () => void
}
const chipsStyle: CSSProperties = { display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }
const chipStyle: CSSProperties = { minHeight: '2.25rem', padding: '0 var(--space-3)', border: `${HAIRLINE} solid var(--color-border-subtle)`, borderRadius: 'var(--radius-pill)', background: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)', cursor: 'pointer' }
const excludeChipStyle: CSSProperties = { ...chipStyle, color: 'var(--color-status-danger)', textDecorationLine: 'line-through' }
const clearStyle: CSSProperties = { ...chipStyle, border: 'none', color: 'var(--color-accent-primary)', background: 'transparent' }

export function ActiveFilterChips(props: ActiveFilterChipsProps): JSX.Element | null {
  const total = props.categories.size + props.attributes.size + props.selectedStores.size
  if (total === 0) return null
  return <div style={chipsStyle} data-testid="deals-active-filters">
    {/* T55.17 — an excluded category reuses the SAME red/struck chip style
        (and "Exclude " prefix convention) an excluded attribute already
        has; an included category keeps the plain chip look. */}
    {Array.from(props.categories).map(([term, intent]) => <button key={`category-${term}`} type="button" style={intent === 'exclude' ? excludeChipStyle : chipStyle} onClick={() => props.onRemoveCategory(term)}>{intent === 'exclude' ? 'Exclude ' : ''}{term} ×</button>)}
    {Array.from(props.attributes).map(([term, intent]) => <button key={`attribute-${term}`} type="button" style={intent === 'exclude' ? excludeChipStyle : chipStyle} onClick={() => props.onRemoveAttribute(term)}>{intent === 'exclude' ? 'Exclude ' : ''}{term} ×</button>)}
    {props.stores.filter((store) => props.selectedStores.has(store.id)).map((store) => <button key={`store-${store.id}`} type="button" style={chipStyle} onClick={() => props.onRemoveStore(store.id)}>{store.name} ×</button>)}
    <button type="button" style={clearStyle} onClick={props.onClear} data-testid="deals-filters-clear">Clear all</button>
  </div>
}
